import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, Sparkles, X } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { computeChoghadiya, formatTime, diffHMS } from "@/lib/choghadiya";

/**
 * TaskMatcher — the headline action of the page.
 * User types what they want to do (or picks a suggestion).
 * We map it to the right intent and show the next good window today.
 *
 * Plain keyword matching, no LLM, no fluff. Tuned for the audience:
 * business, love, deals, legals, parents-for-kids, anyone scheduling
 * something that matters today.
 */
const SUGGESTIONS = [
  "Sign a contract",
  "Negotiation",
  "Propose marriage",
  "Start a business",
  "First date",
  "Job interview",
  "Investment",
  "Buy property",
  "Travel",
  "Anything medical",
  "Filing a case",
];

// Keyword to (state) mapping. Order matters: more specific patterns first.
// We return the recommended state(s) and an optional avoid state.
const RULES = [
  // Government / legal / paperwork
  { match: /(govern|legal|court|case|file|lawsuit|tax|paperwork|visa|passport|register|civic|police|notar)/i,
    prefer: ["udveg_bad", "labh_gain"], avoid: ["rog_evil"], blurb: "for legal and government work" },

  // Negotiation — keep it as its own rule above the wedding/sign rules
  { match: /(negotia|bargain|haggle|settle a deal|counter offer|salary talk|price talk|terms)/i,
    prefer: ["labh_gain", "amrit_best", "shubh_good"], avoid: ["rog_evil", "udveg_bad"], blurb: "to negotiate" },

  // Wedding / engagement / ceremony / vows
  { match: /(wed|marri|propose|engag|ceremon|vow|ritual|naming|housewarm|griha|sacred)/i,
    prefer: ["shubh_good", "amrit_best"], avoid: ["udveg_bad", "kaal_loss", "rog_evil"], blurb: "for ceremonies and vows" },

  // Travel / journey / moving
  { match: /(travel|trip|journey|flight|drive|relocate|move|migrate|depart|leave for)/i,
    prefer: ["char_neutral", "amrit_best"], avoid: ["rog_evil"], blurb: "to start a journey" },

  // Property / real estate / large purchases
  { match: /(propert|real estate|house|home|apartment|flat|land|plot|car|vehicle|expensive|big purchase|buy a)/i,
    prefer: ["shubh_good", "labh_gain", "amrit_best"], avoid: ["udveg_bad", "kaal_loss", "rog_evil"], blurb: "for big purchases" },

  // Money / investment / saving / banking
  { match: /(invest|stock|crypto|trade|deposit|save|saving|bank|finance|loan|mortgage|portfolio|fund)/i,
    prefer: ["labh_gain", "kaal_loss"], avoid: ["rog_evil"], blurb: "for money and accumulation" },

  // Medical / health (covers surgery, doctor, dentist, vaccine, anything medical)
  { match: /(medical|medicine|surg|operation|hospital|doctor|dentist|dental|vaccin|injection|chemo|therapy|heal|health|recover|treat|wellness|checkup|check-up|scan|test result)/i,
    prefer: ["amrit_best", "shubh_good"], avoid: ["rog_evil", "udveg_bad", "kaal_loss"], blurb: "for anything medical" },

  // Sports / competition / fight / debate / argument
  { match: /(sport|game|match|race|compet|fight|debate|argum|defend|attack|win)/i,
    prefer: ["rog_evil", "labh_gain"], avoid: [], blurb: "for competition" },

  // Love / date / partner / connect
  { match: /(love|date|partner|relationship|meet|connect|reunion|romance|kiss|reconcil)/i,
    prefer: ["shubh_good", "amrit_best", "char_neutral"], avoid: ["rog_evil"], blurb: "for love and connection" },

  // Job / interview / career / promotion / pitch / present
  { match: /(job|interview|career|promot|pitch|present|sales|client|deal)/i,
    prefer: ["labh_gain", "amrit_best", "shubh_good"], avoid: ["udveg_bad", "rog_evil"], blurb: "for career and deals" },

  // Sign / contract / agreement / oath / commitment
  { match: /(sign|contract|agreement|oath|commit|seal|approve|notarise|notarize)/i,
    prefer: ["labh_gain", "amrit_best", "shubh_good"], avoid: ["udveg_bad", "kaal_loss", "rog_evil"], blurb: "to sign or commit" },

  // Learning / education / course / class / exam
  { match: /(learn|class|course|exam|study|school|college|degree|certif|teach|train)/i,
    prefer: ["labh_gain", "amrit_best"], avoid: ["rog_evil"], blurb: "for learning" },

  // Business / launch / startup / open
  { match: /(business|startup|launch|open|venture|company|firm|build|product|service|brand)/i,
    prefer: ["labh_gain", "amrit_best", "shubh_good"], avoid: ["udveg_bad", "rog_evil"], blurb: "to start something new" },

  // Pray / meditate / give / donate / spiritual
  { match: /(pray|meditat|spirit|donate|give|charit|temple|puja|fast|seva|alms)/i,
    prefer: ["amrit_best", "shubh_good"], avoid: ["rog_evil"], blurb: "for prayer and giving" },
];

const STATE_HEX = {
  amrit_best:    "#9FCBC6",
  shubh_good:    "#E8B3C0",
  labh_gain:     "#E5C982",
  char_neutral:  "#D8D6CC",
  udveg_bad:     "#A89CBC",
  kaal_loss:     "#968D9F",
  rog_evil:      "#B05A4F",
};

const STATE_LABEL = {
  amrit_best:    "best",
  shubh_good:    "good",
  labh_gain:     "gain",
  char_neutral:  "neutral",
  udveg_bad:     "official",
  kaal_loss:     "savings",
  rog_evil:      "competitive",
};

function matchTask(text) {
  if (!text || text.trim().length < 3) return null;
  for (const r of RULES) {
    if (r.match.test(text)) return r;
  }
  return null;
}

export default function TaskMatcher({ onSlotClick }) {
  const { location } = useLocation();
  const [query, setQuery] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [now, setNow] = useState(new Date());
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const cho = useMemo(
    () => computeChoghadiya(location.lat, location.lng, now, location.timezone),
    [location.lat, location.lng, location.timezone, now]
  );

  const result = useMemo(() => {
    const rule = matchTask(query);
    if (!rule) return null;
    const all = [...cho.day, ...cho.night].sort((a, b) => a.start - b.start);
    const upcoming = all.filter((s) => s.end > now);

    // Best match: the next upcoming slot whose stateKey is in rule.prefer,
    // ordered by the preference index.
    let best = null;
    for (const pref of rule.prefer) {
      const found = upcoming.find((s) => s.stateKey === pref);
      if (found) { best = found; break; }
    }

    // Secondary suggestion: a backup if first preference isn't until late.
    let backup = null;
    if (best) {
      for (const pref of rule.prefer) {
        const found = upcoming.find(
          (s) => s.stateKey === pref &&
                 s !== best &&
                 s.start.getTime() > best.end.getTime()
        );
        if (found) { backup = found; break; }
      }
    }

    // First "to avoid" window today.
    const avoidWindow = rule.avoid.length > 0
      ? upcoming.find((s) => rule.avoid.includes(s.stateKey))
      : null;

    return { rule, best, backup, avoidWindow };
  }, [query, cho, now]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setShowResult(true);
  };
  const reset = () => { setQuery(""); setShowResult(false); inputRef.current?.focus(); };

  return (
    <div className="relative w-full max-w-[760px] mx-auto px-4 sm:px-6">
      <form onSubmit={handleSubmit} className="relative" data-testid="task-matcher-form">
        <div
          className="flex items-center gap-2 rounded-2xl border transition-all"
          style={{
            background: "rgba(14,31,30,0.65)",
            borderColor: query ? "rgba(216,185,122,0.45)" : "rgba(216,185,122,0.25)",
            backdropFilter: "blur(14px)",
            boxShadow: query ? "0 10px 28px -10px rgba(216,185,122,0.35)" : "none",
          }}
        >
          <Search size={16} className="ml-4 shrink-0" style={{ color: "var(--accent-soft)" }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowResult(false); }}
            placeholder="What do you want to do today?"
            data-testid="task-matcher-input"
            className="flex-1 bg-transparent border-0 outline-none py-3.5 sm:py-4 text-sm sm:text-base font-display placeholder:opacity-50"
            style={{ color: "var(--ink)" }}
          />
          {query && (
            <button
              type="button"
              onClick={reset}
              className="w-8 h-8 mr-1.5 rounded-full flex items-center justify-center transition-colors hover:bg-[rgba(216,185,122,0.10)]"
              style={{ color: "var(--ink-muted)" }}
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
          <button
            type="submit"
            disabled={!query.trim()}
            data-testid="task-matcher-submit"
            className="mr-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl flex items-center gap-1.5 disabled:opacity-40 transition-all"
            style={{
              background: "linear-gradient(135deg, #E5C982 0%, #B89B5E 100%)",
              color: "#1A2E2C",
              fontWeight: 600,
            }}
          >
            <span className="hidden sm:inline font-mono uppercase tracking-[0.18em] text-[10px]">when</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </form>

      {/* Suggestion chips (always visible until user types) */}
      {!showResult && (
        <div className="mt-3 sm:mt-4 flex flex-wrap justify-center gap-1.5 sm:gap-2" data-testid="task-matcher-suggestions">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => { setQuery(s); setShowResult(true); }}
              className="px-3 py-1.5 rounded-full border text-[11px] sm:text-xs transition-all hover:translate-y-[-1px]"
              style={{
                borderColor: "rgba(216,185,122,0.25)",
                background: "rgba(14,31,30,0.4)",
                color: "var(--ink-2)",
                backdropFilter: "blur(8px)",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Result card */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            data-testid="task-matcher-result"
            className="mt-4 sm:mt-5 rounded-2xl overflow-hidden"
            style={{
              background: "rgba(14,31,30,0.7)",
              border: "1px solid rgba(216,185,122,0.35)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 18px 42px -16px rgba(0,0,0,0.45)",
            }}
          >
            {result?.best ? (
              <ResultCard
                rule={result.rule}
                best={result.best}
                backup={result.backup}
                avoidWindow={result.avoidWindow}
                now={now}
                tz={location.timezone}
                onSlotClick={onSlotClick}
              />
            ) : !result ? (
              <div className="p-5 sm:p-6 text-center">
                <div className="font-mono uppercase tracking-[0.22em] text-[10px] mb-2" style={{ color: "var(--ink-muted)" }}>
                  no match
                </div>
                <p className="font-display text-sm sm:text-base" style={{ color: "var(--ink-2)" }}>
                  Try a few more words. Or pick one of the suggestions below.
                </p>
              </div>
            ) : (
              <div className="p-5 sm:p-6 text-center">
                <div className="font-mono uppercase tracking-[0.22em] text-[10px] mb-2" style={{ color: "var(--ink-muted)" }}>
                  nothing left today
                </div>
                <p className="font-display text-sm sm:text-base" style={{ color: "var(--ink-2)" }}>
                  No good window remains today {result.rule.blurb}. Try tomorrow.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ResultCard({ rule, best, backup, avoidWindow, now, tz, onSlotClick }) {
  const color = STATE_HEX[best.stateKey];
  const liveNow = now >= best.start && now < best.end;
  const diff = liveNow ? diffHMS(best.end, now) : diffHMS(best.start, now);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <Sparkles size={13} style={{ color: "var(--accent-soft)" }} />
        <span className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: "var(--accent-soft)" }}>
          {rule.blurb}, the best time is
        </span>
      </div>

      {/* Primary window */}
      <button
        onClick={() => onSlotClick?.(best)}
        data-testid="task-matcher-primary"
        className="w-full text-left rounded-xl p-3.5 sm:p-4 transition-colors hover:bg-[rgba(216,185,122,0.06)]"
        style={{
          background: `linear-gradient(135deg, ${color}22 0%, rgba(14,31,30,0.4) 100%)`,
          border: `1px solid ${color}66`,
        }}
      >
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-2 h-2 rounded-full pulse-ring" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
              <span className="font-mono uppercase tracking-[0.18em] text-[9px] sm:text-[10px]" style={{ color }}>
                {liveNow ? "right now" : "next window"} · {STATE_LABEL[best.stateKey]}
              </span>
            </div>
            <div
              className="font-display flex items-baseline gap-2 flex-wrap"
              style={{ color: "var(--ink)", fontSize: "clamp(1.2rem, 2.4vw, 1.6rem)", lineHeight: 1.1, fontWeight: 500 }}
            >
              <span>{formatTime(best.start, tz)}</span>
              <span className="font-mono text-[10px] opacity-60">→</span>
              <span style={{ color: "var(--ink-muted)" }}>{formatTime(best.end, tz)}</span>
            </div>
            <div
              className="font-display italic mt-1"
              style={{ color: "var(--ink-muted)", fontSize: "clamp(0.78rem, 1.3vw, 0.9rem)" }}
            >
              {best.name}, ruled by {best.ruler}
            </div>
          </div>
          {diff && (
            <div className="text-right shrink-0">
              <div className="font-mono uppercase tracking-[0.16em] text-[9px]" style={{ color: "var(--ink-faint)" }}>
                {liveNow ? "ends in" : "starts in"}
              </div>
              <div className="numeric" style={{ color, fontSize: "clamp(0.95rem, 1.8vw, 1.2rem)", letterSpacing: "0.04em" }}>
                {diff.h > 0 ? `${diff.h}h ${String(diff.m).padStart(2, "0")}m` : `${diff.m}m ${String(diff.s).padStart(2, "0")}s`}
              </div>
            </div>
          )}
        </div>
      </button>

      {/* Backup suggestion */}
      {backup && (
        <button
          onClick={() => onSlotClick?.(backup)}
          data-testid="task-matcher-backup"
          className="w-full text-left mt-2 sm:mt-2.5 rounded-xl p-3 transition-colors hover:bg-[rgba(216,185,122,0.06)]"
          style={{
            border: "1px solid rgba(216,185,122,0.18)",
            background: "rgba(14,31,30,0.3)",
          }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATE_HEX[backup.stateKey] }} />
              <span className="font-mono uppercase tracking-[0.16em] text-[9px]" style={{ color: "var(--ink-muted)" }}>
                or later
              </span>
              <span className="font-display" style={{ color: "var(--ink-2)", fontSize: "0.95rem" }}>
                {formatTime(backup.start, tz)} <span style={{ color: "var(--ink-faint)" }}>to</span> {formatTime(backup.end, tz)}
              </span>
            </div>
            <span className="font-mono italic text-[10px]" style={{ color: STATE_HEX[backup.stateKey] }}>
              {backup.name}
            </span>
          </div>
        </button>
      )}

      {/* Avoid window */}
      {avoidWindow && (
        <div className="mt-3 px-3 py-2 rounded-lg" style={{ background: "rgba(176,90,79,0.08)", border: "1px solid rgba(176,90,79,0.25)" }}>
          <span className="font-mono uppercase tracking-[0.16em] text-[9px] mr-2" style={{ color: "#D88A7E" }}>
            avoid
          </span>
          <span className="font-display text-xs sm:text-sm" style={{ color: "var(--ink-2)" }}>
            {formatTime(avoidWindow.start, tz)} to {formatTime(avoidWindow.end, tz)}. {avoidWindow.name} time is not friendly to this.
          </span>
        </div>
      )}
    </div>
  );
}
